import { useState, useRef, useEffect, memo } from "react";
import {
  Send,
  PiggyBank,
  TrendingUp,
  ArrowRight,
  MapPin,
  ExternalLink,
  HandCoins,
  Banknote,
  Heart,
  Briefcase,
  ThumbsUp,
  ThumbsDown,
  LogOut,
  MessageSquarePlus,
  Download,
  RotateCcw,
  Globe,
  Maximize2,
  Store,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatContent } from "../components/ChatContent";
import { SurveyModal } from "../components/chat/SurveyModal";
import { useHealthCheck } from "../hooks/useHealthCheck";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
  secondaryLight: "#f1f2f2",
};

const CEBUANA_LINKS = {
  branchLocator: "https://www.cebuanalhuillier.com/branch-locator/",
  microSavings: "https://www.cebuanalhuillier.com/microsavings/",
  kanegosyo: "https://kanegosyocenter.com/",
};

// Same quick-start cards as ChatPage
const QUICK_START_CARDS = [
  {
    id: "save",
    icon: PiggyBank,
    title: { default: "I want to save money", english: "I want to save money" },
    subtitle: { default: "Paano mag-ipon", english: "Practical saving tips" },
    prompt: {
      default: "Gusto ko matutong mag-ipon ng pera. Ano ang practical tips mo?",
      english: "I want to learn how to save money. What are your practical tips?",
      bisaya: "Gusto ko makakat-on mag-ipon og kwarta. Unsa imong practical tips?",
      ilokano: "Kayat ko matukod ag-ipon ti kuarta. Ania dagiti practical tips mo?",
      bikolano: "Gusto ko makanood mag-ipon nin kwarta. Ano an practical tips mo?",
    },
    color: "#22c55e",
  },
  {
    id: "cash",
    icon: HandCoins,
    title: { default: "I need quick cash", english: "I need quick cash" },
    subtitle: { default: "Kailangan ko ng pera agad", english: "Quick cash options" },
    prompt: {
      default: "Kailangan ko ng pera agad. Ano ang mga options ko?",
      english: "I need cash right now. What are my options?",
      bisaya: "Kinahanglan nako og kwarta karon dayon. Unsa akong mga options?",
      ilokano: "Kasapulak ti kuarta ita. Ania dagiti options ko?",
      bikolano: "Kaipuhan ko nin kwarta ngunyan. Ano an mga options ko?",
    },
    color: "#f59e0b",
  },
  {
    id: "budget",
    icon: Banknote,
    title: { default: "Help me budget", english: "Help me budget" },
    subtitle: { default: "Lagi nauubos sweldo", english: "Salary budgeting" },
    prompt: {
      default: "Lagi nauubos ang sweldo ko. Paano ba mag-budget ng maayos?",
      english: "My salary always runs out. How do I budget properly?",
      bisaya: "Kanunay ra maubos ang akong suweldo. Unsaon man pag-budget?",
      ilokano: "Kanayon a maibus ti suweldo ko. Kasano ti umiso a pag-budget?",
      bikolano: "Pirme naubos an sweldo ko. Paano mag-budget nin tama?",
    },
    color: "#3b82f6",
  },
  {
    id: "debt",
    icon: Banknote,
    title: { default: "Help me with my debt", english: "Help me with my debt" },
    subtitle: { default: "Tulong sa utang ko", english: "Debt payoff planning" },
    prompt: {
      default: "May utang ako at gusto ko mag-plano kung paano bayaran. Paano?",
      english: "I have debt and I want to plan how to pay it off. How do I start?",
      bisaya: "Naa koy utang ug gusto nako mag-plano unsaon pagbayad. Unsaon?",
      ilokano: "Adda utang ko ken kayat ko ti plano tapno mabayadan. Kasano?",
      bikolano: "Igwa akong utang asin gusto ko mag-plano kun paano babayaran. Paano?",
    },
    color: "#ec474e",
  },
  {
    id: "business",
    icon: Briefcase,
    title: { default: "Start a business", english: "Start a business" },
    subtitle: { default: "Negosyo tips", english: "Business startup tips" },
    prompt: {
      default: "Gusto ko mag-start ng maliit na negosyo. Ano ang tips mo?",
      english: "I want to start a small business. What are your tips?",
      bisaya: "Gusto ko magsugod og gamay nga negosyo. Unsa imong tips?",
      ilokano: "Kayat ko mangrugi ti bassit a negosyo. Ania dagiti tips mo?",
      bikolano: "Gusto ko mag-start nin sadit na negosyo. Ano an tips mo?",
    },
    color: "#8b5cf6",
  },
  {
    id: "protect",
    icon: Heart,
    title: { default: "Protect my family", english: "Protect my family" },
    subtitle: { default: "Insurance at seguro", english: "Affordable insurance" },
    prompt: {
      default: "Ano ang affordable na insurance para protektahan ang pamilya ko?",
      english: "What affordable insurance options can protect my family?",
      bisaya: "Unsa ang affordable nga insurance para mapanalipdan ang akong pamilya?",
      ilokano: "Ania ti affordable nga insurance tapno masalakniban ti pamilya ko?",
      bikolano: "Ano an affordable na insurance para maprotektahan an pamilya ko?",
    },
    color: "#ec4899",
  },
];

const LANGUAGE_OPTIONS = [
  { id: "taglish", label: "Taglish", flag: "\u{1F1F5}\u{1F1ED}" },
  { id: "english", label: "English", flag: "\u{1F1FA}\u{1F1F8}" },
  { id: "tagalog", label: "Tagalog", flag: "\u{1F1F5}\u{1F1ED}" },
  { id: "bisaya", label: "Bisaya", flag: "\u{1F1F5}\u{1F1ED}" },
  { id: "ilokano", label: "Ilokano", flag: "\u{1F1F5}\u{1F1ED}" },
  { id: "bikolano", label: "Bikolano", flag: "\u{1F1F5}\u{1F1ED}" },
];

const COMMON_SHORTCUTS = [
  "How can I reduce expenses?",
  "Paano mag-start ng emergency fund?",
  "Ano ang best way to pay off debt?",
];

const localize = (field, lang) => {
  if (typeof field === "string") return field;
  return field[lang] || field["default"] || Object.values(field)[0];
};

const markdownComponents = {
  h1: ({ children }) => <h1 className="text-sm font-bold mb-1.5 mt-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mb-1.5 mt-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-xs font-semibold mb-1 mt-1.5">{children}</h3>,
  p: ({ children }) => <p className="mb-1.5 last:mb-0 text-[13px] leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-1.5 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-1.5 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-[13px]">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: COLORS.primaryRed }}>
      {children}
      <ExternalLink size={9} />
    </a>
  ),
  code: ({ children }) => <code className="bg-white/20 px-1 py-0.5 rounded text-[11px] font-mono">{children}</code>,
  hr: () => <hr className="my-2 border-white/20" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded border border-white/20">
      <table className="w-full text-[11px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-white/10 text-left">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-white/10">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-white/5">{children}</tr>,
  th: ({ children }) => <th className="px-2 py-1 font-semibold">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1">{children}</td>,
};

const MoneyGuroAvatar = memo(({ size = 28 }) => (
  <div
    className="flex items-center justify-center rounded-full overflow-hidden flex-shrink-0"
    style={{ width: size, height: size }}
  >
    <img src="/moneyguro-logo.png" alt="Money Guro" style={{ width: size, height: size, objectFit: "contain" }} />
  </div>
));

const TypingIndicator = memo(() => (
  <div className="flex items-start gap-2 mb-3">
    <MoneyGuroAvatar size={26} />
    <div
      className="text-white rounded-2xl rounded-tl-none flex items-center gap-1 py-2 px-3"
      style={{ backgroundColor: COLORS.primaryDark }}
    >
      <div className="w-1.5 h-1.5 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="w-1.5 h-1.5 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="w-1.5 h-1.5 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
));

const ChatMessage = memo(({ message, index, isLast, onRate, rating }) => (
  <div
    className={`flex flex-col mb-2.5 ${message.role === "user" ? "items-end" : "items-start"}`}
  >
    <div className={`flex max-w-[90%] ${message.role === "user" ? "justify-end" : "justify-start"}`}>
      {message.role === "assistant" && <MoneyGuroAvatar size={26} />}
      <div
        className={`rounded-2xl p-2.5 ${
          message.role === "user"
            ? "bg-white text-[#1A1A1A] border border-gray-200 rounded-tr-none"
            : "text-white rounded-tl-none ml-1.5"
        }`}
        style={message.role === "assistant" ? { backgroundColor: COLORS.primaryDark } : {}}
        data-testid={`embed-msg-${index}`}
      >
        <div className="text-[13px] leading-relaxed">
          {message.role === "assistant" ? (
            <ChatContent content={message.content} MarkdownComponent={ReactMarkdown} markdownProps={{ components: markdownComponents, remarkPlugins: [remarkGfm] }} compact />
          ) : (
            <span className="whitespace-pre-wrap">{message.content}</span>
          )}
        </div>
      </div>
    </div>
    {message.role === "assistant" && message.message_id && (
      <div className="flex items-center gap-1.5 ml-8 mt-1" data-testid={`embed-rating-${index}`}>
        <div className="flex items-center gap-0.5 bg-white rounded-full border border-gray-100 shadow-sm px-0.5 py-0.5">
          <button
            onClick={() => onRate(message.message_id, "up")}
            disabled={!!rating}
            className={`p-1 rounded-full transition-all ${
              rating === "up" ? "bg-green-100 text-green-600" : rating ? "text-gray-200 cursor-default" : "text-gray-400 hover:text-green-600 hover:bg-green-50"
            }`}
            data-testid={`embed-rate-up-${index}`}
          >
            <ThumbsUp size={12} fill={rating === "up" ? "currentColor" : "none"} />
          </button>
          <button
            onClick={() => onRate(message.message_id, "down")}
            disabled={!!rating}
            className={`p-1 rounded-full transition-all ${
              rating === "down" ? "bg-red-100 text-red-500" : rating ? "text-gray-200 cursor-default" : "text-gray-400 hover:text-red-500 hover:bg-red-50"
            }`}
            data-testid={`embed-rate-down-${index}`}
          >
            <ThumbsDown size={12} fill={rating === "down" ? "currentColor" : "none"} />
          </button>
        </div>
        {rating && <span className="text-[9px] text-gray-400">Salamat!</span>}
        {isLast && (
          <a
            href={CEBUANA_LINKS.branchLocator}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 text-white text-[10px] font-medium rounded-full transition-all shadow-sm hover:opacity-90"
            style={{ background: `linear-gradient(to right, ${COLORS.primaryRed}, #f25f64)` }}
            data-testid="embed-find-branch-btn"
          >
            <MapPin size={10} />
            Find Branch
            <ExternalLink size={9} />
          </a>
        )}
      </div>
    )}
  </div>
));

const QuickStartCard = memo(({ card, onSelect, userLang }) => {
  const Icon = card.icon;
  return (
    <button
      onClick={() => onSelect(localize(card.prompt, userLang || "default"))}
      className="flex items-center gap-2.5 w-full p-2.5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left group"
      data-testid={`embed-qs-${card.id}`}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${card.color}15` }}
      >
        <Icon size={16} style={{ color: card.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[13px] text-[#1A1A1A] truncate">{localize(card.title, userLang || "default")}</p>
        <p className="text-[10px] text-[#888] truncate">{localize(card.subtitle, userLang || "default")}</p>
      </div>
      <ArrowRight size={14} className="text-[#ccc] group-hover:text-[#888] flex-shrink-0 transition-colors" />
    </button>
  );
});

// Messages
const PRIVACY_CONSENT_MSG = {
  role: "assistant",
  content: "Kumusta! Ako si **Money Guro**, ang iyong Filipino Financial Advisor.\n\nBago tayo mag-start, kailangan ko muna ng iyong consent:\n\nBy providing your consent, you agree to the collection, use, processing, and sharing of your personal data by PJ Lhuillier Inc. with its affiliates, partners, and third-party contractors, in accordance with applicable laws and regulations. Your personal data will be protected through appropriate security measures, retained only for as long as necessary, and securely disposed of once the purpose has been fulfilled. You may exercise your rights under the Data Privacy Act of 2012 at any time. To learn more, please review our detailed Privacy Policy by clicking the [link](https://cebuanalhuillier.com/privacy-policy/).",
  isConsent: true,
};

const GREETING_MSG = {
  role: "assistant",
  content: "Salamat sa iyong consent! Ano ang pangalan mo?",
};

const CONSENT_DECLINED_MSG = {
  role: "assistant",
  content: "Naiintindihan namin. Hindi namin mapoproseso ang iyong request nang walang consent. Kung magbago ang isip mo, bumalik ka lang anytime. Salamat!",
};

const makeLangPicker = (name) => ({
  role: "assistant",
  content: `Nice to meet you, **${name}**! What language do you prefer?`,
  isLangPicker: true,
});

const makeWelcome = (name, lang) => ({
  role: "assistant",
  content:
    lang === "english"
      ? `Great, **${name}**! I'll chat with you in English. What financial concern can I help you with? Pick a topic below or type your own question!`
      : lang === "bisaya"
      ? `Nice, **${name}**! Mag-chat ta in Bisaya. Unsay financial concern nimo? Pili sa topics sa ubos o i-type ang imong pangutana!`
      : lang === "ilokano"
      ? `Nice, **${name}**! Mag-chat tayo in Ilokano. Ania ti financial concern mo? Pumili ka iti topics dita baba wenno i-type mo ti saludsod mo!`
      : lang === "bikolano"
      ? `Nice, **${name}**! Mag-chat kita in Bikolano. Ano an financial concern mo? Pumili sa mga topics sa baba o mag-type kan sadiri mong hapot!`
      : lang === "tagalog"
      ? `Nice to meet you, **${name}**! Mag-uusap tayo sa Tagalog. Ano ang gusto mong pag-usapan? Pumili sa mga topics sa baba o mag-type ng sarili mong tanong!`
      : `Nice to meet you, **${name}**! Handa na akong tumulong sa'yo.\n\nAno ang gusto mong pag-usapan? Pumili sa mga topics below, o mag-type ng sarili mong tanong!`,
});

const makeFarewell = (name, messages) => {
  const topics = messages
    .filter((m) => m.role === "user" && m.message_id === undefined)
    .slice(-5)
    .map((m) => m.content)
    .filter((c) => c.length > 10);
  const summary = topics.length > 0 ? "\n\nKey takeaway natin today: focus on your financial goals, one step at a time." : "";
  return {
    role: "assistant",
    content: `Salamat sa pag-chat${name ? `, **${name}**` : ""}! Sana nakatulong ako sa'yo.${summary}\n\n**Action step mo today:** Start with the first tip we discussed - even small steps count!\n\nKung may tanong ka pa, andito lang si Money Guro. Omsim!`,
  };
};

const EmbedChatPage = () => {
  const healthStatus = useHealthCheck();
  const [messages, setMessages] = useState([PRIVACY_CONSENT_MSG]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [contextPrompts, setContextPrompts] = useState([]);
  const [userName, setUserName] = useState(null);
  const [userLang, setUserLang] = useState(null);
  const [awaitingConsent, setAwaitingConsent] = useState(true);
  const [awaitingName, setAwaitingName] = useState(false);
  const [awaitingLang, setAwaitingLang] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const [ratings, setRatings] = useState({});
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // URL params for customization
  const params = new URLSearchParams(window.location.search);
  const hideHeader = params.get("hideHeader") === "true";
  const hideBranding = params.get("hideBranding") === "true";

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 100) + "px";
    }
  }, [inputValue]);

  // Notify parent frame
  useEffect(() => {
    try {
      window.parent.postMessage({ type: "moneyguro-state", hasMessages: messages.length > 0 }, "*");
    } catch {}
  }, [messages.length]);

  const sendMessage = async (messageText, { skipAutoDetect = false } = {}) => {
    if (!messageText.trim() || isLoading || chatEnded || awaitingConsent) return;

    const userMessage = { role: "user", content: messageText.trim(), timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setContextPrompts([]);

    // Name capture
    if (awaitingName) {
      const name = messageText.trim().split(/\s+/).slice(0, 3).join(" ");
      setUserName(name);
      setAwaitingName(false);
      setAwaitingLang(true);
      setMessages((prev) => [...prev, makeLangPicker(name)]);
      return;
    }

    setIsLoading(true);
    setShowTopics(false);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageText.trim(),
        session_id: sessionId,
        user_name: userName,
        language: userLang,
      });

      setSessionId(response.data.session_id);

      if (!skipAutoDetect) {
        const detectedLang = response.data.detected_language;
        if (detectedLang && detectedLang !== userLang) {
          const langNames = { bisaya: "Bisaya", ilokano: "Ilokano", bikolano: "Bikolano", tagalog: "Tagalog", english: "English", taglish: "Taglish" };
          setUserLang(detectedLang);
          toast(`Nag-switch tayo sa ${langNames[detectedLang] || detectedLang}!`, { duration: 3000 });
        }
      }

      const botMessage = {
        role: "assistant",
        content: response.data.response,
        message_id: response.data.message_id,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);

      if (response.data.chat_ended || response.data.source === "system") {
        setChatEnded(true);
        setContextPrompts([]);
        if (!surveySubmitted) setTimeout(() => setShowSurvey(true), 1200);
        return;
      }

      if (response.data.source !== "off_topic") {
        axios
          .post(`${API}/chat/suggestions`, {
            session_id: response.data.session_id,
            last_bot_response: response.data.response,
          })
          .then((res) => {
            if (res.data.suggestions?.length) setContextPrompts(res.data.suggestions);
          })
          .catch(() => {});
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      let chatMsg = "Pasensya na, may technical difficulty tayo ngayon. Subukan mo ulit mamaya!";
      if (status === 504 || detail?.includes("too long")) {
        chatMsg = "Pasensya na, medyo matagal ang response ngayon. Subukan mo ulit, lods!";
      } else if (status === 429) {
        chatMsg = "Medyo busy tayo ngayon, lods. Subukan mo ulit in a few seconds!";
      }
      setMessages((prev) => [...prev, { role: "assistant", content: chatMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleConsent = (accepted) => {
    setAwaitingConsent(false);
    if (accepted) {
      setAwaitingName(true);
      setMessages((prev) => [...prev, { role: "user", content: "I agree" }, GREETING_MSG]);
    } else {
      setChatEnded(true);
      setMessages((prev) => [...prev, { role: "user", content: "I decline" }, CONSENT_DECLINED_MSG]);
    }
  };

  const handleLangSelect = (langId) => {
    setUserLang(langId);
    setAwaitingLang(false);
    const langLabel = LANGUAGE_OPTIONS.find((l) => l.id === langId)?.label || langId;
    setMessages((prev) => [...prev, { role: "user", content: langLabel }, makeWelcome(userName, langId)]);
  };

  const handleRate = async (messageId, rating) => {
    if (ratings[messageId]) return;
    setRatings((prev) => ({ ...prev, [messageId]: rating }));
    try {
      await axios.post(`${API}/chat/rate`, { message_id: messageId, rating });
    } catch {}
  };

  const handleEndChat = () => {
    setChatEnded(true);
    setMessages((prev) => [...prev, makeFarewell(userName, prev)]);
    if (sessionId && messages.length > 2 && !surveySubmitted) {
      setTimeout(() => setShowSurvey(true), 800);
    }
  };

  const resetChat = () => {
    setMessages([PRIVACY_CONSENT_MSG]);
    setSessionId(null);
    setRatings({});
    setUserName(null);
    setUserLang(null);
    setAwaitingConsent(true);
    setAwaitingName(false);
    setAwaitingLang(false);
    setShowTopics(false);
    setChatEnded(false);
    setContextPrompts([]);
    setShowSurvey(false);
    setSurveySubmitted(false);
    setPendingAction(null);
  };

  const handleNewChat = () => {
    if (sessionId && messages.length > 2 && !surveySubmitted) {
      setPendingAction("new_chat");
      setShowSurvey(true);
      return;
    }
    resetChat();
  };

  const handleBackToTopics = () => {
    setShowTopics(true);
    setContextPrompts([]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const exportChatHistory = () => {
    if (messages.length <= 1) return;
    const lines = [`# Money Guro Chat - ${new Date().toLocaleDateString()}\n`];
    for (const msg of messages) {
      if (msg.role === "user") lines.push(`**You:** ${msg.content}\n`);
      else if (msg.role === "assistant") lines.push(`**Money Guro:** ${msg.content}\n`);
    }
    lines.push(`\n---\n*Exported from Money Guro by Cebuana Lhuillier*`);
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `money-guro-chat-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Chat exported!");
  };

  const transferSession = async () => {
    if (!sessionId || !userName) return null;
    try {
      const res = await axios.post(`${API}/chat/transfer`, { session_id: sessionId, user_name: userName });
      return res.data.token;
    } catch {
      return null;
    }
  };

  const handleSurveyClose = () => {
    setShowSurvey(false);
    if (pendingAction === "new_chat") resetChat();
    setPendingAction(null);
  };

  const handleSurveySubmitted = () => {
    setSurveySubmitted(true);
    setShowSurvey(false);
    if (pendingAction === "new_chat") resetChat();
    setPendingAction(null);
  };

  const hasApiMessages = messages.some((m) => m.message_id);
  const showQuickCards = !awaitingConsent && !awaitingName && !awaitingLang && !chatEnded && (!hasApiMessages || showTopics);

  return (
    <>
      <div className="h-screen flex flex-col" style={{ backgroundColor: COLORS.secondaryLight }} data-testid="embed-chat">
        {/* Header */}
        {!hideHeader && (
          <header
            className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0"
            style={{ background: `linear-gradient(to right, ${COLORS.primaryDark}, ${COLORS.secondaryBlue})` }}
          >
            <img src="/moneyguro-logo.png" alt="Money Guro" className="h-7 w-auto object-contain" data-testid="embed-header-logo" />
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
              <p className="text-white/70 text-[10px]">Your Filipino Financial Advisor</p>
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  healthStatus === "online" ? "bg-emerald-400" : healthStatus === "offline" ? "bg-red-400 animate-pulse" : "bg-white/30"
                }`}
                title={healthStatus === "online" ? "Connected" : healthStatus === "offline" ? "Connection issue" : "Checking..."}
                data-testid="embed-health-indicator"
              />
            </div>
            <div className="flex items-center gap-1">
              {hasApiMessages && (
                <button onClick={exportChatHistory} className="text-white/50 hover:text-white/80 transition-colors p-1" data-testid="embed-export-btn" title="Export chat">
                  <Download size={13} />
                </button>
              )}
              {!awaitingName && !awaitingConsent && !chatEnded && (
                <button onClick={handleEndChat} className="text-white/50 hover:text-white/80 transition-colors p-1" data-testid="embed-end-chat-btn" title="End Chat">
                  <LogOut size={13} />
                </button>
              )}
              {!awaitingName && !awaitingConsent && (
                <button onClick={handleNewChat} className="text-white/50 hover:text-white/80 transition-colors p-1" data-testid="embed-new-chat-btn" title="New Chat">
                  <MessageSquarePlus size={13} />
                </button>
              )}
              <button
                onClick={async () => {
                  const token = await transferSession();
                  window.open(token ? `${BACKEND_URL}/?t=${token}` : BACKEND_URL, "_blank");
                }}
                className="text-white/50 hover:text-white/80 transition-colors p-1"
                data-testid="embed-open-full"
                title="Open in full page"
              >
                <Maximize2 size={13} />
              </button>
            </div>
          </header>
        )}

        {/* Messages */}
        <main className="flex-1 overflow-y-auto px-3 py-3">
          {messages.map((message, index) => (
            <div key={index}>
              <ChatMessage
                message={message}
                index={index}
                isLast={index === messages.length - 1 && !chatEnded}
                onRate={handleRate}
                rating={message.message_id ? ratings[message.message_id] : null}
              />
              {/* Privacy Consent Buttons */}
              {message.isConsent && awaitingConsent && (
                <div className="flex gap-2 ml-9 mt-2 mb-3" data-testid="embed-consent-picker">
                  <button
                    onClick={() => handleConsent(true)}
                    className="flex items-center gap-1 px-4 py-1.5 text-[12px] font-semibold rounded-full transition-all hover:scale-105 text-white"
                    style={{ backgroundColor: COLORS.primaryDark }}
                    data-testid="embed-consent-yes"
                  >
                    Yes, I agree
                  </button>
                  <button
                    onClick={() => handleConsent(false)}
                    className="flex items-center gap-1 px-4 py-1.5 text-[12px] font-semibold rounded-full border-2 transition-all hover:scale-105"
                    style={{ borderColor: COLORS.primaryRed, color: COLORS.primaryRed }}
                    data-testid="embed-consent-no"
                  >
                    No, I decline
                  </button>
                </div>
              )}
              {/* Language Picker */}
              {message.isLangPicker && awaitingLang && (
                <div className="flex flex-wrap gap-1.5 ml-9 mt-1.5 mb-3" data-testid="embed-lang-picker">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => handleLangSelect(lang.id)}
                      className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full border-2 transition-all hover:scale-105"
                      style={{ borderColor: COLORS.primaryDark, color: COLORS.primaryDark, backgroundColor: `${COLORS.primaryDark}08` }}
                      data-testid={`embed-lang-${lang.id}`}
                    >
                      <Globe size={10} />
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Quick Start Cards */}
          {showQuickCards && (
            <div className="w-full space-y-2 my-3" data-testid="embed-quickstart">
              {QUICK_START_CARDS.map((card) => (
                <QuickStartCard
                  key={card.id}
                  card={card}
                  userLang={userLang}
                  onSelect={(prompt) => {
                    setShowTopics(false);
                    sendMessage(prompt, { skipAutoDetect: true });
                  }}
                />
              ))}
            </div>
          )}

          {/* End-of-chat CTA */}
          {chatEnded && (
            <div className="flex justify-center mt-3 mb-2">
              <button
                onClick={handleNewChat}
                className="flex items-center gap-1.5 text-white text-[12px] rounded-full px-5 py-2 font-medium shadow-md"
                style={{ backgroundColor: COLORS.primaryDark }}
                data-testid="embed-restart-btn"
              >
                <MessageSquarePlus size={14} />
                Start New Chat
              </button>
            </div>
          )}

          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </main>

        {/* Input Area */}
        <footer className="border-t border-gray-100 px-2.5 py-2 flex-shrink-0" style={{ backgroundColor: COLORS.secondaryLight }}>
          {/* Context Prompts + Back to Topics */}
          {!awaitingName && !awaitingLang && !chatEnded && userName && hasApiMessages && !showTopics && (() => {
            const prompts = contextPrompts.length > 0 ? contextPrompts : COMMON_SHORTCUTS;
            return (
              <div className="space-y-1.5 mb-2">
                <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-0.5 px-0.5">
                  {prompts.map((prompt, index) => (
                    <button
                      key={`${prompt}-${index}`}
                      className="whitespace-nowrap text-[10px] flex items-center gap-1 px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      onClick={() => sendMessage(prompt, { skipAutoDetect: true })}
                      data-testid={`embed-context-prompt-${index}`}
                    >
                      <ArrowRight size={9} style={{ color: COLORS.primaryDark }} />
                      {prompt}
                    </button>
                  ))}
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={handleBackToTopics}
                    className="flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full transition-colors hover:bg-gray-100"
                    style={{ color: COLORS.secondaryBlue }}
                    data-testid="embed-back-to-topics-btn"
                  >
                    <RotateCcw size={9} />
                    Back to Topics
                  </button>
                </div>
              </div>
            );
          })()}

          {chatEnded ? (
            <p className="text-center text-[10px] text-gray-400 py-1">Chat ended. Click "Start New Chat" above.</p>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(inputValue); }} className="flex items-end gap-1.5 bg-white rounded-2xl shadow-lg p-1.5">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={awaitingConsent ? "Please select an option above..." : awaitingName ? "Type your name..." : awaitingLang ? "Or type your preferred language..." : "Magtanong kay Money Guro..."}
                className="flex-1 bg-transparent border-none outline-none px-2.5 py-1.5 text-[13px] text-[#1A1A1A] placeholder-gray-400 resize-none"
                disabled={isLoading}
                rows={1}
                style={{ maxHeight: 100, minHeight: 32 }}
                data-testid="embed-chat-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="flex items-center justify-center text-white rounded-full px-3 py-1.5 text-[12px] font-medium disabled:opacity-40 flex-shrink-0"
                style={{ backgroundColor: COLORS.primaryDark }}
                data-testid="embed-send-btn"
              >
                <Send size={14} />
              </button>
            </form>
          )}

          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-1.5 mt-1.5">
            <a
              href={CEBUANA_LINKS.branchLocator}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.primaryDark}15`, color: COLORS.primaryDark }}
            >
              <MapPin size={8} />
              Find Branch
            </a>
            <a
              href={CEBUANA_LINKS.kanegosyo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.primaryRed}15`, color: COLORS.primaryRed }}
            >
              <Store size={8} />
              Kanegosyo Center
            </a>
            <a
              href={CEBUANA_LINKS.microSavings}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.secondaryBlue}15`, color: COLORS.secondaryBlue }}
            >
              <PiggyBank size={8} />
              Savings
            </a>
          </div>

          {!hideBranding && (
            <p className="text-center text-[9px] text-gray-400 mt-1">
              Powered by{" "}
              <a href="https://kanegosyocenter.com" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: COLORS.primaryDark }}>
                Kanegosyo Center
              </a>
              {" "}&bull;{" "}
              <a href="https://www.cebuanalhuillier.com" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: COLORS.primaryRed }}>
                Cebuana Lhuillier
              </a>
            </p>
          )}
        </footer>
      </div>

      {showSurvey && (
        <SurveyModal sessionId={sessionId} userName={userName} onClose={handleSurveyClose} onSubmitted={handleSurveySubmitted} />
      )}
    </>
  );
};

export default EmbedChatPage;
